import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { MapPin, Edit, Trash2, Eye, Plus } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import DataTable from '../../components/admin/DataTable';
import LoadingState from '../../components/admin/LoadingState';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import toast from 'react-hot-toast';

const Locations: React.FC = () => {
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{isOpen: boolean, location: any}>({
    isOpen: false,
    location: null
  });
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Gestion des Emplacements | Admin';
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      toast.error('Erreur lors du chargement des emplacements');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (location: any) => {
    navigate(`/admin/locations/edit/${location.id}`);
  };

  const handleView = (location: any) => {
    // For stations, open the stations page
    if (location.type === 'station') {
      window.open('/stations', '_blank');
    } else {
      // For other location types, just show a toast for now
      toast.info(`Visualisation de ${location.name}`);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.location) return;
    
    try {
      const { error } = await supabase
        .from('locations')
        .delete()
        .eq('id', deleteConfirm.location.id);

      if (error) throw error;
      
      setLocations(locations.filter(l => l.id !== deleteConfirm.location.id));
      toast.success('Emplacement supprimé avec succès');
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Erreur lors de la suppression de l\'emplacement');
    } finally {
      setDeleteConfirm({ isOpen: false, location: null });
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Nom',
      render: (value: string, item: any) => (
        <div className="flex items-center">
          <div className="w-10 h-10 flex-shrink-0 mr-3">
            <div className="w-10 h-10 rounded-md bg-primary-100 flex items-center justify-center">
              <MapPin size={20} className="text-primary-600" />
            </div>
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-xs text-gray-500">{item.address}</div>
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'type',
      header: 'Type',
      render: (value: string) => {
        let bgColor = 'bg-gray-100';
        let textColor = 'text-gray-700';
        
        if (value === 'store') {
          bgColor = 'bg-blue-100';
          textColor = 'text-blue-700';
        } else if (value === 'station') {
          bgColor = 'bg-green-100';
          textColor = 'text-green-700';
        } else if (value === 'partner') {
          bgColor = 'bg-purple-100';
          textColor = 'text-purple-700';
        }
        
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${bgColor} ${textColor}`}>
            {value}
          </span>
        );
      },
      sortable: true,
      width: 'w-32'
    },
    {
      key: 'coordinates',
      header: 'Coordonnées',
      render: (value: any) => {
        // Check if value exists and has the expected structure
        if (!value || typeof value !== 'object') {
          return 'N/A';
        }
        
        // Make sure x and y are numbers before using toFixed
        const x = typeof value.x === 'number' ? value.x.toFixed(4) : 'N/A';
        const y = typeof value.y === 'number' ? value.y.toFixed(4) : 'N/A';
        
        return `${x}, ${y}`;
      },
      width: 'w-40'
    },
    {
      key: 'created_at',
      header: 'Créé le',
      render: (value: string) => new Date(value).toLocaleDateString('fr-FR'),
      sortable: true,
      width: 'w-32'
    }
  ];

  return (
    <div className="px-6">
      <PageHeader
        title="Emplacements"
        description="Gérez les emplacements (magasins, stations, partenaires)"
        actionLink="/admin/locations/new"
        actionLabel="Ajouter un emplacement"
        actionIcon={<Plus size={16} />}
      />
      
      {loading ? (
        <LoadingState type="table" />
      ) : (
        <DataTable
          columns={columns}
          data={locations}
          onEdit={handleEdit}
          onDelete={(location) => setDeleteConfirm({ isOpen: true, location })}
          onView={handleView}
          searchPlaceholder="Rechercher un emplacement..."
          searchKey="name"
          emptyTitle="Aucun emplacement"
          emptyDescription="Vous n'avez pas encore ajouté d'emplacement."
          emptyActionLabel="Ajouter un emplacement"
          onEmptyAction={() => navigate('/admin/locations/new')}
        />
      )}
      
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Supprimer l'emplacement"
        message={`Êtes-vous sûr de vouloir supprimer l'emplacement "${deleteConfirm.location?.name}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, location: null })}
        type="danger"
      />
    </div>
  );
};

export default Locations;